import { useMemo } from 'react';

import { useProductsListingStore } from '../store/products-listing.store';
import type { ProductFilterValues, ProductTab } from '../types/catalog-operations.types';

import { useBatchHeader } from './use-batch-detail';
import { useCurationProducts } from './use-curation-products';

interface UseProductSiblingsParams {
  /** Batch whose products listing the detail page was opened from. */
  batchId: string;
  /** Product currently on screen — located within the listing to derive position. */
  productId: string;
  /** Listing tab the user came from, forwarded by the route's search params.
   *  Drives the implicit `needs_attention` filter, exactly as on the listing. */
  tab?: ProductTab | undefined;
}

/** A product the pager can move to, plus the listing page it lives on. */
export interface ProductSibling {
  productId: string;
  batchId: string;
  /** 1-based listing page — differs from the current one when crossing an edge. */
  page: number;
}

/** Where the open product sits in the listing, and whether resolving a neighbour
 *  requires the adjacent page. Pure so the arithmetic is testable without a
 *  QueryClient — see `use-product-siblings.test.ts`. */
export interface SiblingSlice {
  /** Index within the current page's rows; `-1` when the product isn't on it. */
  index: number;
  /** 1-based position across the whole filtered list, or `null` when unresolved. */
  position: number | null;
  /** Pages the filtered list spans at this page size. */
  pageCount: number;
  /** Product is the first row of a page that has a predecessor. */
  needsPreviousPage: boolean;
  /** Product is the last row of a page that has a successor. */
  needsNextPage: boolean;
}

/**
 * Locates `productId` within one page of listing rows. Split out of the hook so
 * the off-by-one-prone parts — the position formula and the two edge flags — can
 * be exercised directly.
 */
export function resolveSiblingSlice(
  productIds: readonly string[],
  productId: string,
  page: number,
  pageSize: number,
  total: number,
): SiblingSlice {
  const index = productIds.indexOf(productId);
  // `pageSize` is store-supplied; guard against a 0 that would divide to Infinity.
  const pageCount = pageSize > 0 ? Math.ceil(total / pageSize) : 0;
  return {
    index,
    position: index < 0 ? null : (page - 1) * pageSize + index + 1,
    pageCount,
    needsPreviousPage: index === 0 && page > 1,
    needsNextPage: index >= 0 && index === productIds.length - 1 && page < pageCount,
  };
}

interface UseProductSiblingsResult {
  /** 1-based position across the whole filtered list, or `null` when unresolved. */
  position: number | null;
  /** Total products matching the listing's filters/search. */
  total: number;
  previous: ProductSibling | null;
  next: ProductSibling | null;
  /** True while the neighbouring page needed to cross an edge is still loading. */
  isLoadingEdge: boolean;
}

/**
 * Locates the open product within the products listing so the detail page can
 * page through siblings. Rebuilds the listing's own query from
 * `useProductsListingStore` (sessionStorage-persisted for this reason, so a
 * refresh or a direct hit on the detail URL doesn't lose it), so in the normal
 * listing → detail flow this resolves off the existing cache entry rather than
 * issuing a request.
 *
 * Returns an empty result — and so hides the pager — whenever that listing state
 * isn't available: a link opened in a fresh tab, or a store scoped to a different
 * batch, whose filters would describe a list this product isn't part of.
 */
export function useProductSiblings({
  batchId,
  productId,
  tab,
}: UseProductSiblingsParams): UseProductSiblingsResult {
  // Only trust the store while it's scoped to this batch — `ensureBatch` resets
  // it per batch, and `null` means the listing page never rendered.
  const isListingScoped = useProductsListingStore((s) => s.batchId) === batchId;
  const appliedFilters = useProductsListingStore((s) => s.filters);
  const search = useProductsListingStore((s) => s.search);
  const sortBy = useProductsListingStore((s) => s.sortBy);
  const sortDir = useProductsListingStore((s) => s.sortDir);
  const pagination = useProductsListingStore((s) => s.pagination);
  const page = pagination.page;
  const pageSize = pagination.pageSize;

  // Only reads header fields (`curationRefId`, `isTrial`) — the light phase-1
  // header, not the heavy `/jobs/{id}` aggregate.
  const { data: batch } = useBatchHeader(batchId, { enabled: isListingScoped });
  const curationRefId = batch?.curationRefId;
  const isTrial = batch?.isTrial ?? false;

  // Mirrors the listing's tab → filter derivation, including the trial fallback,
  // so the query key matches and the listing's cached page is reused as-is.
  const effectiveTab: ProductTab =
    isTrial && tab === 'needs-attention' ? 'newly-enriched' : (tab ?? 'newly-enriched');
  const effectiveFilters = useMemo<ProductFilterValues>(
    () => ({
      needs_attention: false,
      ...appliedFilters,
      ...(effectiveTab === 'needs-attention' ? { needs_attention: true } : {}),
    }),
    [appliedFilters, effectiveTab],
  );

  const listingRefId = isListingScoped ? curationRefId : undefined;
  const { data: currentPage } = useCurationProducts({
    curationRefId: listingRefId,
    batchId,
    page,
    size: pageSize,
    filters: effectiveFilters,
    search,
    sortBy,
    sortDir,
  });

  const products = currentPage?.products ?? [];
  const total = currentPage?.total ?? 0;
  // Edge crossings need the adjacent page, fetched only when actually at an edge.
  // `useCurationProducts` is gated on `curationRefId`, so withholding the id is
  // how the query stays disabled the rest of the time.
  const { index, position, needsPreviousPage, needsNextPage } = resolveSiblingSlice(
    products.map((p) => p.id),
    productId,
    page,
    pageSize,
    total,
  );
  const { data: previousPage, isFetching: isFetchingPrevious } = useCurationProducts({
    curationRefId: needsPreviousPage ? listingRefId : undefined,
    batchId,
    page: page - 1,
    size: pageSize,
    filters: effectiveFilters,
    search,
    sortBy,
    sortDir,
  });
  const { data: nextPage, isFetching: isFetchingNext } = useCurationProducts({
    curationRefId: needsNextPage ? listingRefId : undefined,
    batchId,
    page: page + 1,
    size: pageSize,
    filters: effectiveFilters,
    search,
    sortBy,
    sortDir,
  });

  if (!isListingScoped || index < 0) {
    return { position: null, total: 0, previous: null, next: null, isLoadingEdge: false };
  }

  let previous: ProductSibling | null = null;
  if (index > 0) {
    const sibling = products[index - 1];
    if (sibling) previous = { productId: sibling.id, batchId: sibling.batchId, page };
  } else if (needsPreviousPage) {
    // Backwards over an edge lands on the last row of the previous page.
    const sibling = previousPage?.products.at(-1);
    if (sibling) previous = { productId: sibling.id, batchId: sibling.batchId, page: page - 1 };
  }

  let next: ProductSibling | null = null;
  if (index < products.length - 1) {
    const sibling = products[index + 1];
    if (sibling) next = { productId: sibling.id, batchId: sibling.batchId, page };
  } else if (needsNextPage) {
    const sibling = nextPage?.products[0];
    if (sibling) next = { productId: sibling.id, batchId: sibling.batchId, page: page + 1 };
  }

  return {
    position,
    total,
    previous,
    next,
    isLoadingEdge: (needsPreviousPage && isFetchingPrevious) || (needsNextPage && isFetchingNext),
  };
}
